import { useState, useCallback, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { Utensils, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/AppContext';
import type { Locale } from '@/config/locales';
import { waiterCallApi, ApiClientError } from '@/lib/api';
import type { StoreWifiInfo, WaiterCallItem } from '@/lib/api/waiter-calls';
import type { WaiterCallRequestItem } from '@/lib/api/types';
import {
  RequestItemButton,
  WifiInfoSection,
  waiterRequestTranslations,
  requestItems,
  copyToClipboard,
} from './waiter-request';
import type { WaiterRequestModalProps, WaiterRequestTranslations } from './waiter-request';

// 타입 재익스포트
export type { WaiterRequestModalProps } from './waiter-request';

/**
 * 서버 항목(apiItems)의 안정적 선택 식별자 (#1761).
 * 매장 설정 항목은 id, 기본 카탈로그 항목은 default_key 기반.
 */
function waiterItemSelectionId(apiItem: WaiterCallItem): string {
  return apiItem.id != null ? `id:${apiItem.id}` : `key:${apiItem.default_key ?? ''}`;
}

const LEADING_EMOJI_LABEL_RE =
  /^(\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?)*)\s+(.+)$/u;

function waiterItemDisplay(apiItem: WaiterCallItem): { emoji: string | null; label: string } {
  const emoji = apiItem.emoji?.trim();
  if (emoji) {
    return { emoji, label: apiItem.label };
  }

  const label = apiItem.label.trim();
  const match = label.match(LEADING_EMOJI_LABEL_RE);
  if (!match) {
    return { emoji: null, label: apiItem.label };
  }

  return { emoji: match[1], label: match[2].trim() };
}

const WAITER_LABEL_ALIASES: Record<string, Partial<Record<Locale, string>>> = {
  palillos: {
    ko: '젓가락',
    ru: 'Палочки',
  },
  casa: {
    ko: '집',
    ru: 'Дом',
  },
  rapido: {
    ko: '빠르게',
    ru: 'Быстро',
  },
  'rápido': {
    ko: '빠르게',
    ru: 'Быстро',
  },
};

function localizedWaiterItemLabel(
  label: string,
  locale: Locale,
  translations: WaiterRequestTranslations,
): string {
  const normalizedLabel = label.trim().toLocaleLowerCase('es-MX');
  const defaultEntry = Object.entries(waiterRequestTranslations.es.items).find(
    ([, esLabel]) => esLabel.toLocaleLowerCase('es-MX') === normalizedLabel,
  );
  if (defaultEntry) {
    const [key] = defaultEntry;
    return translations.items[key as keyof WaiterRequestTranslations['items']];
  }

  return WAITER_LABEL_ALIASES[normalizedLabel]?.[locale] ?? label;
}

export function WaiterRequestModal({
  isOpen,
  onClose,
  restaurantName: _restaurantName,
  storeId,
  tableNumber: initialTableNumber,
  wifi,
}: WaiterRequestModalProps) {
  // #732 — 화면과 API 모두 15 locale 기준. es 는 admin 정준 locale 인 es-MX 로 정규화한다.
  const { locale } = useLanguage();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [message, setMessage] = useState('');
  const [tableNumber, setTableNumber] = useState(initialTableNumber || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // #451 + PR #472 R1 시정: initialTableNumber 가 비동기로 *처음* 채워질 때만 동기화.
  // 사용자가 직접 입력 중인 값을 prop 변경이 덮어쓰지 않도록 first-fill 검출 사용.
  // (직전 prop 이 falsy 이고 신규 prop 이 truthy 일 때만 setTableNumber)
  const prevInitialRef = useRef(initialTableNumber);
  useEffect(() => {
    if (! prevInitialRef.current && initialTableNumber) {
      setTableNumber(initialTableNumber);
    }
    prevInitialRef.current = initialTableNumber;
  }, [initialTableNumber]);
  // #1431 cross-repo — admin#1467 BE 매장별 호출 항목 fetch. fallback: 하드코딩 requestItems.
  const [apiItems, setApiItems] = useState<WaiterCallItem[] | null>(null);
  // #1761 — 항목 출처 상태. apiItems=null 의미 과적재(로딩/미설정/실패)를 분리한다.
  //  - 'loading': fetch 진행 중 → 선택 UI 미노출(stale 선택 방지).
  //  - 'server' : 정상 응답. apiItems 있으면 설정 항목, 없으면(data=[]) 미설정 → default_key 정당.
  //  - 'fallback': fetch 실패 → 매장 설정 여부 불명 → 구조화 전송 불가, legacy 라벨 접기로 안전 처리.
  const [itemSource, setItemSource] = useState<'loading' | 'server' | 'fallback'>('loading');
  // #1432 cross-repo — admin#1480 BE 매장 WiFi fetch (테이블 세션 인증 손님만).
  // prop wifi 우선, 없을 때만 fetch. 미설정 매장은 null 유지 → WifiInfoSection 미렌더.
  const [fetchedWifi, setFetchedWifi] = useState<StoreWifiInfo | null>(null);

  const t = waiterRequestTranslations[locale];
  const apiLocale = locale === 'es' ? 'es-MX' : locale;

  useEffect(() => {
    if (! isOpen) return;
    // 재오픈/매장 변경 시 로딩 상태로 초기화하고 직전 선택을 비운다(stale 방지).
    setItemSource('loading');
    setApiItems(null);
    setSelectedItems([]);
    waiterCallApi
      .listItems(storeId, apiLocale)
      .then((response) => {
        setApiItems(response.data?.length ? response.data : null);
        setItemSource('server');
      })
      .catch(() => {
        // fetch 실패 — 매장 설정 여부 불명. 하드코딩 항목을 보여주되 default_key 미전송.
        setApiItems(null);
        setItemSource('fallback');
      });
  }, [isOpen, storeId, apiLocale]);

  // #1432 cross-repo — 모달 open + prop wifi 미입력 시 BE WiFi endpoint fetch.
  useEffect(() => {
    if (! isOpen) return;
    if (wifi) {
      // prop 우선 — fetch skip
      setFetchedWifi(null);
      return;
    }

    waiterCallApi
      .getWifi(storeId)
      .then((response) => {
        if (response.data?.ssid) {
          setFetchedWifi(response.data);
        } else {
          setFetchedWifi(null);
        }
      })
      .catch(() => {
        // 404 (미설정) / 401 / 403 / 429 → WiFi 섹션 미렌더
        setFetchedWifi(null);
      });
  }, [isOpen, storeId, wifi]);

  const effectiveWifi = wifi ?? fetchedWifi;

  // #1432 cross-repo — prop wifi 우선, 없으면 fetched wifi 사용. 둘 다 없으면 섹션 미노출.
  const wifiSSID = effectiveWifi?.ssid ?? '';
  const wifiPassword = effectiveWifi?.password ?? '';

  const handleToggleItem = useCallback((itemId: string) => {
    setSelectedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  }, []);

  const handleCopyPassword = useCallback(async () => {
    const success = await copyToClipboard(wifiPassword);
    if (success) {
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    }
  }, [wifiPassword]);

  const handleConnectWifi = useCallback(async () => {
    const wifiInfo = `${t.wifiName}: ${wifiSSID}\n${t.wifiPassword}: ${wifiPassword}`;
    const success = await copyToClipboard(wifiInfo);

    if (success) {
      toast.success(t.wifiCopied || 'WiFi info copied!', {
        description: `${t.wifiName}: ${wifiSSID}`,
      });
    } else {
      toast.info(`${t.wifiName}: ${wifiSSID}`, {
        description: `${t.wifiPassword}: ${wifiPassword}`,
      });
    }
  }, [t, wifiSSID, wifiPassword]);

  const handleSendRequest = useCallback(async () => {
    setError(null);

    // 테이블 번호 필수 체크
    if (!tableNumber.trim()) {
      setError(t.tableNumberRequired);
      return;
    }

    // 최소 1개 항목 또는 메시지 필요
    if (selectedItems.length === 0 && !message.trim()) {
      setError(t.selectAtLeastOne);
      return;
    }

    setIsSubmitting(true);

    try {
      // #1761 — 출처별 전송 분기.
      //  - 'fallback'(fetch 실패): 매장 설정 여부 불명 → 구조화 default_key 전송 시 admin
      //    게이트와 충돌(422) 위험 → legacy 라벨 접기로 message 에 합쳐 안전 전송.
      //  - 'server': 라벨 접기 폐기, 구조화(item_id|default_key) 전송 + raw message.
      let items: WaiterCallRequestItem[] = [];
      let legacyItemLabels = '';

      if (itemSource === 'fallback') {
        legacyItemLabels = selectedItems
          .map((id) => t.items[id as keyof typeof t.items] ?? '')
          .filter(Boolean)
          .join(', ');
      } else {
        items = selectedItems
          .map((selectionId): WaiterCallRequestItem | null => {
            if (apiItems) {
              const found = apiItems.find(
                (apiItem) => waiterItemSelectionId(apiItem) === selectionId,
              );
              if (!found) {
                return null;
              }
              return found.id != null
                ? { item_id: found.id }
                : { default_key: found.default_key ?? '' };
            }
            // server + apiItems 없음(data=[]) → 매장 미설정 → default_key 정당(admin 게이트 통과).
            return { default_key: selectionId };
          })
          .filter((item): item is WaiterCallRequestItem => item !== null);
      }

      const fullMessage = [legacyItemLabels, message.trim()].filter(Boolean).join(' - ');

      await waiterCallApi.callWaiter(storeId, {
        table_number: tableNumber.trim(),
        message: fullMessage || undefined,
        message_locale: fullMessage ? apiLocale : undefined,
        items: items.length > 0 ? items : undefined,
      });

      toast.success(t.requestSent);

      // Reset and close
      setSelectedItems([]);
      setMessage('');
      if (!initialTableNumber) {
        setTableNumber('');
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.code === 'DUPLICATE_CALL') {
          setError(t.duplicateCall);
        } else {
          setError(err.message || t.error);
        }
      } else {
        setError(t.error);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [tableNumber, selectedItems, message, t, storeId, initialTableNumber, onClose, apiItems, itemSource, apiLocale]);

  const handleClose = useCallback(() => {
    setSelectedItems([]);
    setPasswordCopied(false);
    setMessage('');
    setError(null);
    if (!initialTableNumber) {
      setTableNumber('');
    }
    onClose();
  }, [onClose, initialTableNumber]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[min(90vw,380px)] bg-background border-border rounded-2xl p-0 overflow-hidden">
        <DialogHeader className="bg-muted p-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-primary/10">
              <Utensils className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle className="text-[15px] text-foreground">
              {t.title}
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">{t.subtitle}</DialogDescription>
        </DialogHeader>

        <div className="p-4 space-y-3 max-h-[65vh] overflow-y-auto scrollbar-hide">
          {/* Table Number Input */}
          {!initialTableNumber && (
            <div className="space-y-1.5">
              <label className="text-[13px] font-medium text-foreground">
                {t.tableNumber}
              </label>
              <input
                type="text"
                value={tableNumber}
                onChange={(e) => setTableNumber(e.target.value)}
                placeholder={t.tableNumberPlaceholder}
                maxLength={20}
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-[13px] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          )}

          {/* Current Table Display */}
          {initialTableNumber && (
            <div className="px-3 py-2 rounded-xl bg-primary/10 border border-primary/20">
              <span className="text-[13px] text-primary font-medium">
                {t.tableNumber}: {initialTableNumber}
              </span>
            </div>
          )}

          {/* Request Items Grid — #1431 cross-repo: API 항목 우선, 없으면 하드코딩 fallback.
              #1761 — fetch 진행 중(loading)에는 선택 UI 대신 스켈레톤을 노출해 stale 선택을 차단. */}
          {itemSource === 'loading' ? (
            <div className="grid grid-cols-2 gap-2" aria-busy="true" aria-live="polite">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton
                  key={`item-skeleton-${i}`}
                  className="h-[72px] rounded-xl border border-border bg-muted/40"
                />
              ))}
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-2">
            {apiItems
              ? apiItems.map((apiItem) => {
                  const selectionId = waiterItemSelectionId(apiItem);
                  const isSelected = selectedItems.includes(selectionId);
                  const displayItem = waiterItemDisplay(apiItem);
                  const label = apiItem.default_key
                    ? undefined
                    : localizedWaiterItemLabel(displayItem.label, locale, t);
                  return (
                    <RequestItemButton
                      key={selectionId}
                      item={{ id: selectionId, icon: null, labelKey: apiItem.default_key ?? 'water' }}
                      selectionId={selectionId}
                      isSelected={isSelected}
                      translations={t}
                      label={label}
                      emoji={displayItem.emoji}
                      onToggle={handleToggleItem}
                    />
                  );
                })
              : requestItems.map((item) => (
                  <RequestItemButton
                    key={item.id}
                    item={item}
                    isSelected={selectedItems.includes(item.id)}
                    translations={t}
                    onToggle={handleToggleItem}
                  />
                ))}
          </div>
          )}

          {/* Message Input */}
          <div className="space-y-2">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t.messagePlaceholder}
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-[13px] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              rows={3}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/20">
              <span className="text-[13px] text-red-500">{error}</span>
            </div>
          )}

          {/* WiFi Section — #1432 cross-repo: prop wifi 또는 BE fetch 결과 있을 때만 노출 */}
          {effectiveWifi && (
            <WifiInfoSection
              wifiSSID={wifiSSID}
              wifiPassword={wifiPassword}
              passwordCopied={passwordCopied}
              translations={t}
              onCopyPassword={handleCopyPassword}
              onConnectWifi={handleConnectWifi}
            />
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-[min(6vw,1.5rem)] pt-0 flex gap-2">
          <Button
            onClick={handleClose}
            variant="outline"
            disabled={isSubmitting}
            className="flex-1 border-border rounded-xl h-10 text-[13px]"
          >
            {t.cancel}
          </Button>
          <Button
            onClick={handleSendRequest}
            disabled={isSubmitting || (!tableNumber.trim() && !initialTableNumber)}
            className="flex-1 bg-gradient-to-r from-primary to-primary-hover hover:from-primary-hover hover:to-primary text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-xl h-10 text-[13px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                {t.sending}
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5 mr-1.5" />
                {t.sendRequest}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
